import React, { useState, useRef, useCallback } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiMove, FiTrash2, FiMessageSquare, FiChevronUp, FiChevronDown, FiArrowUp, FiArrowDown, FiRotateCw, FiCopy, FiEdit3 } = FiIcons

const Canvas = ({ 
  slide, 
  selectedElement, 
  onSelectElement, 
  onUpdateElement, 
  onDeleteElement, 
  onDuplicateElement,
  onElementRightClick,
  onBringToFront,
  onSendToBack,
  onBringForward,
  onSendBackward,
  onDragEnd,
  gridEnabled, 
  snapEnabled 
}) => {
  const [dragState, setDragState] = useState(null)
  const [resizeState, setResizeState] = useState(null)
  const [rotateState, setRotateState] = useState(null)
  const canvasRef = useRef(null)

  const snapToGrid = (value, gridSize = 20) => {
    return snapEnabled ? Math.round(value / gridSize) * gridSize : value
  }

  const handleMouseDown = (e, element, action = 'move') => {
    e.stopPropagation()
    onSelectElement(element.id)

    const rect = canvasRef.current.getBoundingClientRect()
    const startX = e.clientX - rect.left
    const startY = e.clientY - rect.top

    if (action === 'move') {
      setDragState({
        elementId: element.id,
        startX: startX - element.style.position.x,
        startY: startY - element.style.position.y
      })
    } else if (action.startsWith('resize')) {
      setResizeState({
        elementId: element.id,
        startX,
        startY,
        startWidth: element.style.width,
        startHeight: element.style.height,
        startPosX: element.style.position.x,
        startPosY: element.style.position.y,
        corner: action.split('-')[1]
      })
    } else if (action === 'rotate') {
      const elementRect = canvasRef.current.getBoundingClientRect()
      const elementCenterX = element.style.position.x + element.style.width / 2
      const elementCenterY = element.style.position.y + element.style.height / 2
      
      setRotateState({
        elementId: element.id,
        centerX: elementCenterX,
        centerY: elementCenterY,
        startAngle: Math.atan2(startY - elementCenterY, startX - elementCenterX) * 180 / Math.PI,
        startRotation: element.style.rotation || 0
      })
    }
  }

  const handleMouseMove = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    const currentX = e.clientX - rect.left
    const currentY = e.clientY - rect.top

    if (dragState) {
      const newX = snapToGrid(Math.max(0, Math.min(800 - 100, currentX - dragState.startX)))
      const newY = snapToGrid(Math.max(0, Math.min(600 - 50, currentY - dragState.startY)))

      // Update local position immediately for smooth visual feedback
      const element = slide.elements.find(el => el.id === dragState.elementId)
      if (element) {
        element.style = {
          ...element.style,
          position: { x: newX, y: newY }
        }
      }
      
      // Throttled update to database (only every 50ms during drag)
      if (!dragState.lastUpdate || Date.now() - dragState.lastUpdate > 50) {
        onUpdateElement(dragState.elementId, {
          style: {
            ...slide.elements.find(el => el.id === dragState.elementId).style,
            position: { x: newX, y: newY }
          }
        })
        dragState.lastUpdate = Date.now()
      }
    } else if (resizeState) {
      const deltaX = currentX - resizeState.startX
      const deltaY = currentY - resizeState.startY

      let newWidth = resizeState.startWidth
      let newHeight = resizeState.startHeight
      let newX = resizeState.startPosX
      let newY = resizeState.startPosY

      const corner = resizeState.corner

      if (corner.includes('e')) {
        newWidth = Math.max(50, resizeState.startWidth + deltaX)
      }
      if (corner.includes('w')) {
        const widthChange = resizeState.startWidth - deltaX
        if (widthChange >= 50) {
          newWidth = widthChange
          newX = resizeState.startPosX + deltaX
        }
      }
      if (corner.includes('s')) {
        newHeight = Math.max(30, resizeState.startHeight + deltaY)
      }
      if (corner.includes('n')) {
        const heightChange = resizeState.startHeight - deltaY
        if (heightChange >= 30) {
          newHeight = heightChange
          newY = resizeState.startPosY + deltaY
        }
      }

      // Constrain to canvas bounds
      newX = Math.max(0, Math.min(800 - newWidth, newX))
      newY = Math.max(0, Math.min(600 - newHeight, newY))
      newWidth = Math.min(newWidth, 800 - newX)
      newHeight = Math.min(newHeight, 600 - newY)

      // Throttled update to database (only every 50ms during resize)
      if (!resizeState.lastUpdate || Date.now() - resizeState.lastUpdate > 50) {
        onUpdateElement(resizeState.elementId, {
          style: {
            ...slide.elements.find(el => el.id === resizeState.elementId).style,
            width: snapToGrid(newWidth),
            height: snapToGrid(newHeight),
            position: { x: snapToGrid(newX), y: snapToGrid(newY) }
          }
        })
        resizeState.lastUpdate = Date.now()
      }
    } else if (rotateState) {
      const currentAngle = Math.atan2(currentY - rotateState.centerY, currentX - rotateState.centerX) * 180 / Math.PI
      let angleDiff = currentAngle - rotateState.startAngle
      
      // Apply damping factor to make rotation more controlled
      const dampingFactor = 0.8
      angleDiff *= dampingFactor
      
      let newRotation = (rotateState.startRotation + angleDiff) % 360
      
      // Normalize to 0-360 range
      if (newRotation < 0) {
        newRotation += 360
      }
      
      // Snap to 15-degree increments if within 8 degrees (increased threshold for better snapping)
      const snapAngle = Math.round(newRotation / 15) * 15
      if (Math.abs(newRotation - snapAngle) < 8) {
        newRotation = snapAngle
      }

      // Throttled update to database (only every 50ms during rotation)
      if (!rotateState.lastUpdate || Date.now() - rotateState.lastUpdate > 50) {
        onUpdateElement(rotateState.elementId, {
          style: {
            ...slide.elements.find(el => el.id === rotateState.elementId).style,
            rotation: newRotation
          }
        })
        rotateState.lastUpdate = Date.now()
      }
    }
  }, [dragState, resizeState, rotateState, slide.elements, onUpdateElement])

  const handleMouseUp = () => {
    // If we were dragging/resizing/rotating, call onDragEnd to trigger snapshot
    if ((dragState || resizeState || rotateState) && onDragEnd) {
      onDragEnd()
    }
    
    setDragState(null)
    setResizeState(null)
    setRotateState(null)
    // Ensure a final save after editing completes
    if (selectedElement) {
      const element = slide.elements.find(el => el.id === selectedElement)
      if (element) {
        onUpdateElement(selectedElement, { style: { ...element.style } })
      }
    }
  }

  const handleCanvasClick = (e) => {
    if (e.target === canvasRef.current) {
      onSelectElement(null)
    }
  }

  const getBackgroundStyle = () => {
    if (!slide?.background) return '#ffffff'
    
    if (slide.background?.type === 'gradient') {
      return slide.background.value
    } else if (slide.background?.type === 'image') {
      const { url, position = 'center', size = 'cover' } = slide.background
      return `url(${url}) ${position}/${size}`
    } else {
      return slide.background?.value || '#ffffff'
    }
  }

  return (
    <div className="flex justify-center">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden relative">
        {gridEnabled && (
          <div
            className="absolute inset-0 pointer-events-none opacity-10 z-10"
            style={{
              backgroundImage: `
                linear-gradient(to right, #666 1px, transparent 1px),
                linear-gradient(to bottom, #666 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px'
            }}
          />
        )}
        
        <div
          ref={canvasRef}
          className="relative cursor-default slide-canvas"
          data-slide-id={slide?.id}
          data-slide-canvas="true"
          style={{
            width: '800px',
            height: '600px',
            background: getBackgroundStyle()
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={handleCanvasClick}
        >
          {slide.elements
            .sort((a, b) => (a.style.zIndex || 0) - (b.style.zIndex || 0)) // Sort by z-index for proper rendering order
            .map((element) => (
            <Element
              key={element.id}
              element={element}
              isSelected={selectedElement === element.id}
              onMouseDown={(e, action) => handleMouseDown(e, element, action)}
              onRightClick={(e) => onElementRightClick && onElementRightClick(e, element.id)}
              onUpdateElement={onUpdateElement}
              onDeleteElement={onDeleteElement}
              onDuplicateElement={onDuplicateElement}
              onBringToFront={onBringToFront}
              onSendToBack={onSendToBack}
              onBringForward={onBringForward}
              onSendBackward={onSendBackward}
            />
          ))}

          {/* Background editing button */}
          <button
            onClick={() => onSelectElement('background')}
            className="absolute top-2 right-2 w-8 h-8 bg-black bg-opacity-40 hover:bg-opacity-60 rounded-lg flex items-center justify-center transition-all duration-200 z-50"
            title="Edit Background"
          >
            <SafeIcon icon={FiEdit3} className="w-4 h-4 text-white" />
          </button>

          {!selectedElement && (
            <div className="absolute inset-0 pointer-events-none border-2 border-dashed border-gray-300 opacity-0 hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>
    </div>
  )
}

const Element = ({ element, isSelected, onMouseDown, onRightClick, onUpdateElement, onDeleteElement, onDuplicateElement, onBringToFront, onSendToBack, onBringForward, onSendBackward }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(element.content)

  const handleDoubleClick = (e) => {
    e.stopPropagation()
    if (element.type === 'text' || element.type === 'list') {
      setIsEditing(true)
      setEditContent(Array.isArray(element.content) ? element.content.join('\n') : element.content)
    }
  }

  const handleEditSubmit = () => {
    const newContent = element.type === 'list' 
      ? editContent.split('\n').filter(line => line.trim()) 
      : editContent

    onUpdateElement(element.id, { content: newContent })
    setIsEditing(false)
  }

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && element.type === 'text') {
      e.preventDefault()
      handleEditSubmit()
    }
    if (e.key === 'Escape') {
      setIsEditing(false)
      setEditContent(element.content)
    }
  }

  const renderContent = () => {
    if (isEditing) {
      return (
        <textarea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          onBlur={handleEditSubmit}
          onKeyDown={handleEditKeyDown}
          className="w-full h-full resize-none border-none outline-none bg-transparent"
          style={{
            fontSize: element.style.fontSize,
            color: element.style.color,
            textAlign: element.style.textAlign,
            fontWeight: element.style.fontWeight,
            lineHeight: element.style.lineHeight || '1.4'
          }}
          autoFocus
        />
      )
    }

    switch (element.type) {
      case 'text':
        return (
          <div
            className="w-full h-full flex items-center"
            style={{
              fontSize: element.style.fontSize,
              color: element.style.color,
              textAlign: element.style.textAlign,
              fontWeight: element.style.fontWeight,
              lineHeight: element.style.lineHeight || '1.4',
              justifyContent: 
                element.style.textAlign === 'center' ? 'center' :
                element.style.textAlign === 'right' ? 'flex-end' : 'flex-start'
            }}
          >
            {element.content}
          </div>
        )

      case 'list':
        return (
          <ul
            className="w-full h-full"
            style={{
              fontSize: element.style.fontSize,
              color: element.style.color,
              lineHeight: element.style.lineHeight || '1.6'
            }}
          >
            {Array.isArray(element.content) 
              ? element.content.map((item, index) => (
                  <li key={index} className="mb-2">• {item}</li>
                ))
              : <li>• {element.content}</li>
            }
          </ul>
        )

      case 'image':
        return (
          <img
            src={element.src}
            alt="Slide element"
            className="w-full h-full object-cover"
            style={{ borderRadius: element.style.borderRadius }}
            draggable={false}
          />
        )

      case 'video':
        // Helper function to extract YouTube video ID from URL
        const getYouTubeVideoId = (url) => {
          const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)
          return match ? match[1] : null
        }

        const videoId = getYouTubeVideoId(element.videoUrl || '')
        const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null

        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-900 relative">
            {videoId ? (
              <>
                <img
                  src={thumbnailUrl}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                  style={{ borderRadius: element.style.borderRadius }}
                  draggable={false}
                />
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-red-600 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-white text-center">
                <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                <p className="text-sm">No video URL</p>
              </div>
            )}
          </div>
        )

      case 'shape':
        return (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              backgroundColor: element.style.backgroundColor || '#3b82f6',
              borderRadius: element.style.shapeType === 'circle' ? '50%' : element.style.borderRadius || '0px',
              border: element.style.border || 'none',
              clipPath: element.style.shapeType === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : 'none'
            }}
          />
        )

      case 'link':
        return (
          <div
            className="w-full h-full flex items-center cursor-pointer"
            style={{
              fontSize: element.style.fontSize,
              color: element.style.color,
              textAlign: element.style.textAlign,
              fontWeight: element.style.fontWeight,
              lineHeight: element.style.lineHeight || '1.4',
              justifyContent: 
                element.style.textAlign === 'center' ? 'center' :
                element.style.textAlign === 'right' ? 'flex-end' : 'flex-start',
              textDecoration: 'underline'
            }}
            onClick={(e) => {
              e.stopPropagation()
              if (element.href) {
                window.open(element.href, '_blank', 'noopener,noreferrer')
              }
            }}
          >
            {element.content}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={`absolute select-none ${isSelected ? 'ring-2 ring-primary-500 ring-offset-2' : ''}`}
      style={{
        left: element.style.position.x,
        top: element.style.position.y,
        width: element.style.width,
        height: element.style.height,
        zIndex: isSelected ? 999 : (element.style.zIndex || 0), // Use element's z-index
        transform: `rotate(${element.style.rotation || 0}deg)`, // Add rotation support
        transformOrigin: 'center center',
        cursor: isSelected ? 'move' : 'pointer'
      }}
      onMouseDown={(e) => onMouseDown(e, 'move')}
      onDoubleClick={handleDoubleClick}
      onContextMenu={onRightClick}
    >
      {renderContent()}

      {isSelected && (
        <>
          {/* Resize handles */}
          <div
            className="absolute -top-1 -left-1 w-3 h-3 bg-primary-500 border border-white rounded-full cursor-nw-resize"
            onMouseDown={(e) => onMouseDown(e, 'resize-nw')}
          />
          <div
            className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 border border-white rounded-full cursor-ne-resize"
            onMouseDown={(e) => onMouseDown(e, 'resize-ne')}
          />
          <div
            className="absolute -bottom-1 -left-1 w-3 h-3 bg-primary-500 border border-white rounded-full cursor-sw-resize"
            onMouseDown={(e) => onMouseDown(e, 'resize-sw')}
          />
          <div
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-primary-500 border border-white rounded-full cursor-se-resize"
            onMouseDown={(e) => onMouseDown(e, 'resize-se')}
          />

          {/* Rotation handle - positioned at bottom right corner */}
          <div
            className="absolute -bottom-6 -right-6 w-6 h-6 bg-blue-500 border-2 border-white rounded-full cursor-pointer flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg"
            onMouseDown={(e) => onMouseDown(e, 'rotate')}
            title="Drag to rotate"
          >
            <SafeIcon icon={FiRotateCw} className="w-3 h-3 text-white" />
          </div>

          {/* Action buttons toolbar */}
          <div className="absolute -top-12 -left-2 flex items-center space-x-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1">
            {/* Move indicator */}
            <div className="p-1 bg-primary-500 text-white rounded">
              <SafeIcon icon={FiMove} className="w-3 h-3" />
            </div>

            {/* Duplicate element */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDuplicateElement(element.id)
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Duplicate Element"
            >
              <SafeIcon icon={FiCopy} className="w-3 h-3 text-gray-600" />
            </button>

            {/* Layer controls */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                onBringToFront(element.id)
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Bring to Front"
            >
              <SafeIcon icon={FiArrowUp} className="w-3 h-3 text-gray-600" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation()
                onBringForward(element.id)
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Bring Forward"
            >
              <SafeIcon icon={FiChevronUp} className="w-3 h-3 text-gray-600" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation()
                onSendBackward(element.id)
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Send Backward"
            >
              <SafeIcon icon={FiChevronDown} className="w-3 h-3 text-gray-600" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation()
                onSendToBack(element.id)
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Send to Back"
            >
              <SafeIcon icon={FiArrowDown} className="w-3 h-3 text-gray-600" />
            </button>

            {/* TODO: Re-enable comment functionality in future versions
            {onRightClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onRightClick(e)
                }}
                className="p-1 hover:bg-blue-100 rounded"
                title="Add comment"
              >
                <SafeIcon icon={FiMessageSquare} className="w-3 h-3 text-blue-600" />
              </button>
            )}
            */}

            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDeleteElement(element.id)
              }}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete element"
            >
              <SafeIcon icon={FiTrash2} className="w-3 h-3 text-red-600" />
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default Canvas